import { useState, useEffect, KeyboardEvent, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  CircularProgress,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
}

export const FIRST_USE_KEY = 'space-antics-first-use';

export interface ChatInputHandle {
  setValue: (value: string) => void;
  getValue: () => string;
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput({ onSend, disabled }, ref) {
  const [input, setInput] = useState('');

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    setValue: (value: string) => setInput(value),
    getValue: () => input,
  }));

  // Check if this is the first use and set default prompt accordingly
  useEffect(() => {
    const hasUsedChat = localStorage.getItem(FIRST_USE_KEY);
    if (!hasUsedChat) {
      // First use - set default prompt
      setInput("Let's play");
    }
    // Subsequent uses - input stays blank
  }, []);

  const handleSend = () => {
    if (input.trim() && !disabled) {
      // Mark that chat has been used
      localStorage.setItem(FIRST_USE_KEY, 'true');

      onSend(input);
      setInput('');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        alignItems: 'flex-end',
        width: '100%',
      }}
    >
      <TextField
        fullWidth
        multiline
        maxRows={4}
        placeholder="Type a message..."
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        sx={{
          flex: 1,
          minWidth: 0,
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.paper',
          },
        }}
      />
      <IconButton
        color="primary"
        onClick={handleSend}
        disabled={disabled || !input.trim()}
        sx={{
          flexShrink: 0,
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
          '&.Mui-disabled': {
            backgroundColor: 'action.disabledBackground',
          },
          width: 48,
          height: 48,
        }}
      >
        {disabled ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          <SendIcon />
        )}
      </IconButton>
    </Box>
  );
});
